/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { saveToLocalStorage, loadFromLocalStorage, clearLocalStorageItem } from '../common/localStorage';
import { fetchModulesEndPoint } from '../common/constants';
import { asApplicationJsonBody } from '../common/graphqlJsonBody';
import { actionCreators as notificationActionCreator } from '../store/Notification';
// import { history } from '../store/history';
import { createNewUserEndPoint } from '../common/constants';

const loginRequest = 'LOGIN_REQUEST';
const loginSuccess = 'LOGIN_SUCCESS';
const loginFailure = 'LOGIN_FAILURE';
// const logoutRequest = 'LOGOUT_REQUEST';
const userNotFound = 'USER_NOTFOUND';
const loginError = 'LOGIN_ERROR';
const usernamereload ='USERNAME_RELOAD';

const initialState = {
    authenticated: false,
    isSigningIn: false,
    authToken: null,
    error: '',
    isNewUser: false,
    newUserMessage: '',
    username: '',
    userimage:'',
    userNotFound: false
}
export class AuthService {

    public async login() {

        const redirectPath = this.getValidRedirectPath();

        let authorizeEndpoint = `${await this.getTidEndpoint('Auth')}?client_id=${import.meta.env.VITE_CLIENTID}` +
            `&response_type=code` +
            `&scope=${import.meta.env.VITE_CLIENTSCOPE}` +
            `&redirect_uri=${import.meta.env.VITE_REDIRECTURL}` +
            `&code_challenge=${await this.generateCodeChallenge(this.generateCodeVerifier())}` +
            `&code_challenge_method=S256` +
            `&state=${encodeURIComponent(redirectPath)}`;

        window.location.href = authorizeEndpoint;
    }

    private getValidRedirectPath(): string {
        let path = sessionStorage.getItem('redirectAfterLogin') || '/apps/dashboard';
        // Ensure the redirect path is under /apps
        if (!path.startsWith('/apps')) {
            path = '/apps' + (path.startsWith('/') ? path : '/' + path);
        }
        // Normalize any duplicate slashes
        path = path.replace(/\/+/g, '/');
        return path;
    }

    public async CheckAndCreateNewUser(email: string, tuuid: string) {
        let isUserAvailable = false;
        const userId: string = loadFromLocalStorage('AuthToken');
        const headerDict: any = {
            'Authorization': 'Bearer ' + userId,
            'Content-Type': 'application/json'
        };
        const requestBody = {
            Email: email,
            TUUID: tuuid
        };

        try {
            const response = await fetch(createNewUserEndPoint, {
                headers: new Headers(headerDict),
                method: 'POST',
                body: JSON.stringify(requestBody)
            });
            //const responseData = await response.json();
            if (response.status == 200) {
                console.log('data Response' + response);
                isUserAvailable = true;
                await this.requestUserRoleModules();
            }
            else {
                isUserAvailable = false;
            }
        }
        catch
        {
            isUserAvailable = false;
        }
        return isUserAvailable;
    }

    public async GetTokenAndAuthorizeUser(code: string) {
        // const code_verifier = loadFromLocalStorage('code_verifier');
        const details = {
            'grant_type': 'authorization_code',
            code,
            'tenantDomain': 'Trimble.com',
            'redirect_uri': import.meta.env.VITE_REDIRECTURL,
            'client_id': import.meta.env.VITE_CLIENTID,
            'code_verifier': loadFromLocalStorage('code_verifier'),
            'code_challenge': (await this.generateCodeChallenge(this.generateCodeVerifier())),
            'code_challenge_method': 'S256'
        };

        const formBody = Object.entries(details).map(([key, value]) => `${encodeURIComponent(key)  }=${  encodeURIComponent(value)}`).join('&');
        const response = await fetch(await this.getTidEndpoint('Token'), {
            headers: new Headers(
                {
                    'Content-Type': 'application/x-www-form-urlencoded',
                }
            ),
            method: 'POST',
            body: formBody
        });
        const responseData = await response.json();
        if (response.status === 200 && responseData != null) {
            let userInfo = await this.setUserinStorage(responseData);
            let image = this.parseJwt(responseData.id_token).picture;

            return {
                userId: userInfo.sub,
                email: userInfo.email,
                username: `${userInfo.given_name} ${userInfo.family_name}`,
                userimage: image
            };
        }
        else if (response.status === 401 || response.status === 403) {
            return {
                message: 'Invalid credential supplied.'
            };
        }
        else {
            throw new Error(JSON.stringify(responseData));
        }
    }

    public async RefreshToken(refreshToken: string) {
        if (!loadFromLocalStorage('Client Mode') || loadFromLocalStorage('Client Mode') != 'Desktop') {
            const code_verifier = loadFromLocalStorage('code_verifier');
            const details = {
                'grant_type': 'refresh_token',
                'refresh_token': refreshToken,
                'client_id': import.meta.env.VITE_CLIENTID || 'I am error.',
                code_verifier,
                'code_challenge': (await this.generateCodeChallenge(this.generateCodeVerifier())),
                'code_challenge_method': 'S256'
            };

            const formBody = Object.entries(details).map(([key, value]) => encodeURIComponent(key) + '=' + encodeURIComponent(value)).join('&')
            const response = await fetch(await this.getTidEndpoint('Token'), {
                headers: new Headers(
                    {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    }
                ),
                method: 'POST',
                body: formBody
            });
            const responseData = await response.json();
            if (response.status === 200 && responseData != null) {
                clearLocalStorageItem('AuthToken');
                clearLocalStorageItem('RefreshToken');
                clearLocalStorageItem('ExpiryTime');
                clearLocalStorageItem('Id-Token');
                await this.setUserinStorage(responseData);
                return await this.requestUserRoleModules();
            }
            else {
                window.location.assign('/apps/signin');
            }
        }
    }

    public async SignOut() {
        const logoutEndpoint = await this.getTidEndpoint('Logout');
        const idToken = loadFromLocalStorage('Id-Token');
        localStorage.clear();
        window.location.href = `${logoutEndpoint}?` +
            `id_token_hint=${idToken}&post_logout_redirect_uri=${import.meta.env.VITE_LOGOUTURL}`;
    }

    private async setUserinStorage(responseData: any) {
        saveToLocalStorage(responseData.access_token, 'AuthToken');
        saveToLocalStorage(responseData.refresh_token, 'RefreshToken');
        saveToLocalStorage(responseData.id_token, 'Id-Token');

        //set URL for image
        const image = this.parseJwt(responseData.id_token).picture;
        saveToLocalStorage(image, 'UserImage');
        
        //setting 45 min expiry.
        saveToLocalStorage(new Date(new Date().getTime() + 45 * 60000).getTime(), 'ExpiryTime');

        const userInfo = await this.getUserDetails(responseData.access_token);
        saveToLocalStorage(`${userInfo.given_name} ${userInfo.family_name}`, 'UserName');

        clearLocalStorageItem('SelectedTab');
        clearLocalStorageItem('Enabled Modules');
        clearLocalStorageItem('MyRole');
        await notificationActionCreator.getNotificationCount();
        return userInfo;

    }

    private async getUserDetails(token: string) {
        const response = await fetch(await this.getTidEndpoint('UserInfo'), {
            headers: new Headers(
                {
                    'Authorization': 'Bearer ' + token,
                }
            ),
            method: 'GET',
        });

        const responseData = await response.json();
        if (response.status === 200 && responseData != null) {

            return responseData;
        }
    }

    private async getTidEndpoint(name: string) {
        const response = await fetch(import.meta.env.VITE_STSAUTORITY || '/error', {
            method: 'GET',
        });
        const responseData = await response.json();
        if (response.status === 200 && responseData != null) {
            if (name.includes('Token')) {
                return responseData.token_endpoint;
            }
            else if (name.includes('Auth')) {
                return responseData.authorization_endpoint;
            }
            else if (name.includes('UserInfo')) {
                return responseData.userinfo_endpoint;
            }
            else if (name.includes('Logout')) {
                return responseData.end_session_endpoint;
            }
        }
    }

    private generateCodeVerifier() {
        let verifier = Array(128)
            .fill('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-._~')
            .map(x => x[Math.floor(crypto.getRandomValues(new Uint32Array(1))[0] / (0xffffffff + 1) * x.length)])
            .join('');
        saveToLocalStorage(verifier, 'code_verifier');
        return verifier;
    }

    private async generateCodeChallenge(code_verifier: string) {
        const encoder = new TextEncoder();
        const data = encoder.encode(code_verifier);
        const buffer = await window.crypto.subtle.digest('SHA-256', data);
        return btoa(new Uint8Array(buffer).reduce((data, byte) => {
            return data + String.fromCharCode(byte);
        }, '')).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
    }

    private async requestUserRoleModules() {
        const userId: string = loadFromLocalStorage('AuthToken');
        const headerDict: any = {
            'Authorization': 'Bearer ' + userId,
            'Content-Type': 'application/json'
        };
        const queryString = `query{
          myroles
          {
              data{
                  rolename	
                  roleid
              }
          }
          mymodules
          {
              data{
                  id
                  modulename
              }
          }   
      }`

        try {
            if (navigator.onLine) {
                const response = await fetch(fetchModulesEndPoint, {
                    headers: new Headers(headerDict),
                    body: asApplicationJsonBody(queryString),
                    method: 'POST'
                });
                const responseData = await response.json();
                const enabledModules: Array<any> = responseData.data.mymodules.data;
                saveToLocalStorage(enabledModules, 'Enabled Modules');
                const myRole: any = responseData.data.myroles.data;
                saveToLocalStorage(myRole, 'MyRole');
            }
        }
        catch (e) {
            const errorValue: Array<number> = [-99];
            saveToLocalStorage(errorValue, 'Enabled Modules');
        }
    }

    private parseJwt(token: any) {
        let base64Url = token.split('.')[1];
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        let jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    };
}

export const actionCreator = {

    Login: () => async (dispatch: any) => {

        try {
            dispatch({ type: loginRequest });
            new AuthService().login();
        }
        catch {
            dispatch({ type: loginError, error: { message: 'Unexpected error occurred.Please try again later' } });
        }
    },

    AuthorizeUser: (code: string, state: string) => async (dispatch: any) => {
        try {
            dispatch({ type: loginRequest });
            const responseData = await new AuthService().GetTokenAndAuthorizeUser(code);
            if (responseData.userId)
            {
                const isUserAvailable = await new AuthService().CheckAndCreateNewUser(responseData.email, responseData.userId);
                if (isUserAvailable == true)
                {
                    dispatch({ type: loginSuccess, responseData });            
                    console.log(responseData);
                    // Force landing on dashboard under /apps to prevent signin/callback loops
                    window.location.assign('/apps/dashboard');
                }
                else {
                    dispatch({ type: userNotFound, responseData });
                }
            }
            else
            {
                dispatch({ type: loginFailure, responseData });
                new AuthService().SignOut();
            }
            
        }
        catch (e) {
            dispatch({ type: loginError, error: { message: e } });
            new AuthService().SignOut();
        }
    },
     ReloadUserName: () => async (dispatch: any) => {

         try {
             const lcusername = loadFromLocalStorage('UserName');
             dispatch({ type: usernamereload, lcusername });
        }
        catch {
            dispatch({ type: loginError, error: { message: 'Unexpected error occurred.Please try again later' } });
        }
    }
}
export const reducer = (state: any, action: any) => {
    state = state || initialState;
    switch (action.type) {
        case loginSuccess:
            return {
                ...state,
                isSigningIn: false,
                authenticated: true,
                authToken: action.responseData.userId,
                username: action.responseData.username,
                isNewUser: false,
                newUserMessage: '',
                userimage: action.responseData.userimage
            };
        case loginFailure:
            return {
                ...state,
                isSigningIn: false,
                authenticated: false,
                error: action.responseData.message,
                isNewUser: false,
                newUserMessage: ''
            };
        case loginRequest:
            return {
                ...state,
                isSigningIn: true,
                authenticated: false,
                authToken: null,
                isNewUser: false,
                newUserMessage: ''
            };
        case loginError:
            return {
                ...state,
                isSigningIn: false,
                authenticated: false,
                error: action.error.message,
                isNewUser: false,
                newUserMessage: ''
            };
        case usernamereload:
            return {
                ...state,
                username: action.lcusername
            };
        case userNotFound:
            return {
                ...state,
                isSigningIn: false,
                authenticated: true,
                authToken: action.responseData.userId,
                username: action.responseData.username,
                userimage: action.responseData.userimage,
                isNewUser: true,
                error: action.responseData.message,
                userNotFound: true
            };

    }
    return state;
};
